import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs/Observable';
import { publishReplay, refCount } from 'rxjs/operators';
import { Classification, ClassificationDetail, ClassificationType } from '../../models/classification.model';
import { HttpClientService } from '../httpClient/httpclient.service';


@Injectable()
export class ClassificationService {

  list: Classification[];
  types: ClassificationType[];
  details: ClassificationDetail[];

  editing: Classification;
  editingType: ClassificationType;
  editingDetail: ClassificationDetail;

  private cachedFields: Observable<any>;
  private cachedFieldsDetail: Observable<any>;
  private cachedFieldsType: Observable<any>;

  constructor (
    private h: HttpClientService,
    private srvNotify: MessageService,
    private translate: TranslateService
  ) { }

  getTypes(name?: string) {
    let queryString = '';
    if (name) {
      queryString = '?name=' + name;
    }

    return this.h.get('/koncentro-common/classification/type' + queryString)
      .map(res => {
        return this.types = res;
      });
  }

  getClassificationTypes(name?: string) {
    let queryString = '';
    if (name) {
      queryString = '?classification_type_name=' + name;
    }

    return this.h.get('/koncentro-common/classification' + queryString)
      .map(res => {
        return this.types = res;
      });
  }

  getDetails(classificationTypeId: number) {
    return this.h.get('/koncentro-common/classification/' + classificationTypeId + '/detail')
      .map(res => {
        this.details = res;

        return this.details;
      });
  }

  getDetail(classificationId: number) {
    return this.h.get('/koncentro-common/classification/detail/' + classificationId);
  }

  getList(typeId?: number) {
    let queryString = '';
    if(typeId) {
      queryString = '?classification_type_id=' + typeId;
    }

    return this.getTypes().switchMap(() => {
      return this.h.get('/koncentro-common/classification' + queryString).map(res => {
        this.list = res;
        this.list.forEach(c => {
          c.classification_type = this.types.find(t => t.id === c.classification_type_id);
          const parent = this.list.find(l => l.id === c.parent_id);
          if (parent) {
            c.parentName = parent.name;
          }
        });
        return this.list;
      });
    });
  }

  getClassificationsForType(typeId: number) {
    return this.h.get(`/koncentro-common/classification?classification_type_id=${typeId}`);
  }

  getStoreClassifications(storeId?: number){
    return this.h.get('/koncentro-common/store/' + storeId + '/classification');
  }

  post(classification: Classification)  {
    return this.h.post('/koncentro-common/classification', classification).map(res => {
      this.getList().subscribe();
      this.translate.get(['common.success', 'common.operation_success']).subscribe(res => {
        this.srvNotify.add({severity: 'info', summary: res['common.success'], detail: res['common.operation_success']});
      });
    });
  }

  postType(type: ClassificationType)  {
    return this.h.post('/koncentro-common/classification/type', type).map(res => {
      this.getTypes().subscribe();
      this.translate.get(['common.success', 'common.operation_success']).subscribe(res => {
        this.srvNotify.add({severity: 'info', summary: res['common.success'], detail: res['common.operation_success']});
      });
    });
  }

  postDetails(details: ClassificationDetail)  {
    return this.h.post('/koncentro-common/classification/detail', details).map(res => {
      this.getDetails(details.classification_id).subscribe()
      this.translate.get(['common.success', 'common.operation_success']).subscribe(res => {
        this.srvNotify.add({severity: 'info', summary: res['common.success'], detail: res['common.operation_success']});
      });
    });
  }

  put(classification: Classification)  {
    return this.h.put('/koncentro-common/classification', classification).map(res => {
      classification.id = res.id;
      Object.assign(this.editing, classification);
      this.translate.get(['common.success', 'common.operation_success']).subscribe(res => {
        this.srvNotify.add({severity: 'info', summary: res['common.success'], detail: res['common.operation_success']});
      });
    });
  }

  putType(type: ClassificationType)  {
    return this.h.put('/koncentro-common/classification/detail', type).map(res => {
      type.id = res.id;
      Object.assign(this.editingType, type);
      this.translate.get(['common.success', 'common.operation_success']).subscribe(res => {
        this.srvNotify.add({severity: 'info', summary: res['common.success'], detail: res['common.operation_success']});
      });
    });
  }

  putDetails(details: ClassificationDetail)  {
    return this.h.put('/koncentro-common/classification/detail', details).map(res => {
      details.id = res.id;
      Object.assign(this.editingDetail, details);
      this.translate.get(['common.success', 'common.operation_success']).subscribe(res => {
        this.srvNotify.add({severity: 'info', summary: res['common.success'], detail: res['common.operation_success']});
      });
    });
  }

  delete(entry: Classification) {
    return this.h.delete('/koncentro-common/classification/' + entry.id).map(res => {
      this.list.splice(this.list.indexOf(entry), 1);
      this.translate.get(['common.success', 'common.operation_success']).subscribe(res => {
        this.srvNotify.add({severity: 'info', summary: res['common.success'], detail: res['common.operation_success']});
      });
    });
  }

  deleteType(entry: ClassificationType) {
    return this.h.delete('/koncentro-common/classification/type/' + entry.id).map(res => {
      this.getTypes().subscribe();
      this.types.splice(this.types.indexOf(entry), 1);
      this.translate.get(['common.success', 'common.operation_success']).subscribe(res => {
        this.srvNotify.add({severity: 'info', summary: res['common.success'], detail: res['common.operation_success']});
      });
    });
  }

  deleteDetail(entry: ClassificationDetail) {
    return this.h.delete('/koncentro-common/classification/detail/' + entry.id).map(res => {
      this.details.splice(this.details.indexOf(entry), 1);
      this.details = [...this.details];
      this.translate.get(['common.success', 'common.operation_success']).subscribe(res => {
        this.srvNotify.add({severity: 'info', summary: res['common.success'], detail: res['common.operation_success']});
      });
    });
  }

  getFields() {
    if (!this.cachedFields) {
      this.cachedFields = this.h.get('/koncentro-common/schema/ClassificationDto').pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedFields;
  }

  getFieldsDetail() {
    if (!this.cachedFieldsDetail) {
      this.cachedFieldsDetail = this.h.get('/koncentro-common/schema/ClassificationDetailDto').pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedFieldsDetail;
  }

  getFieldsType() {
    if (!this.cachedFieldsType) {
      this.cachedFieldsType = this.h.get('/koncentro-common/schema/ClassificationTypeDto').pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedFieldsType;
  }

}



// WEBPACK FOOTER //
// ./src/app/services/classification/classification.service.ts